import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import CountdownTimer from './CountdownTimer'
import { LottoGameCardProps } from './LottoGameCard.types'
import { useThemeAssets } from '../../hooks/useThemeAssets'
import { ThemeName } from '../../constants/theme'
import { useCurrentEvent } from '../../hooks/useCurrentEvent'
import {
  getLottoGameCardSurfaceClasses,
  getLottoGameCardDecorations,
} from './LottoGameCard.utils'

const LottoGameCard: React.FC<LottoGameCardProps> = ({
  country,
  flagBannerName,
  title,
  closedTime,
  timeRemaining,
  isOpen,
  gameLink,
  activeRoundLink,
}) => {
  const { t } = useTranslation()
  const [isOverdue, setIsOverdue] = React.useState<boolean>(false)
  const isGameOpen = isOpen && !isOverdue
  const event = useCurrentEvent()
  const {
    modelImageUrls,
    bannerFlagImageUrls,
    smallFlagImageUrls,
    themeName,
    svgAssets,
  } = useThemeAssets()
  const modelImage = modelImageUrls[country]
  const bannerImage = bannerFlagImageUrls[flagBannerName]
  const flagImage = smallFlagImageUrls[country]

  const ribbonImage = (svgAssets as { RIBBON?: string | null })?.RIBBON
  const abstractBorderImage = (svgAssets as { ABSTRACT_BORDER?: string | null })
    ?.ABSTRACT_BORDER
  const cardLanternImage = (svgAssets as { CARD_LANTERN?: string | null })
    ?.CARD_LANTERN
  const cardFlagBannerImage = (
    svgAssets as { CARD_FLAG_BANNER?: string | null }
  )?.CARD_FLAG_BANNER

  const surfaceClassName = getLottoGameCardSurfaceClasses({ isGameOpen, event })
  const decorations = getLottoGameCardDecorations({
    isGameOpen,
    event,
    themeName,
    ribbonImage,
    abstractBorderImage,
    cardLanternImage,
    cardFlagBannerImage,
  })

  return (
    <div className="h-full">
      <Link
        to={isGameOpen && activeRoundLink ? activeRoundLink : gameLink}
        className="block h-full"
      >
        <div className={surfaceClassName}>
          {decorations.map((d) => (
            <img key={d.key} src={d.src} alt={d.alt} className={d.className} />
          ))}
          {/* Banner */}
          <div className="relative w-full rounded-xl h-24 sm:h-36 md:h-32 lg:h-40 xl:h-36 2xl:h-44 overflow-hidden">
            <img
              src={bannerImage}
              alt="Banner"
              className={`w-full h-full object-cover ${
                !isGameOpen ? 'grayscale' : ''
              }`}
            />
            {themeName === ThemeName.GOLD && (
              <img
                src={modelImage}
                alt="Model"
                className={`absolute bottom-0 left-2 h-full object-contain ${
                  !isGameOpen ? 'grayscale' : ''
                }`}
              />
            )}
          </div>

          {/* Content */}
          <div className="p-3 sm:p-5 flex flex-col justify-between gap-1 sm:gap-2 flex-1">
            {/* Title & Flag */}
            <div className="flex justify-between items-center mb-1 sm:mb-2 gap-1 sm:gap-2">
              <div className="flex flex-col gap-0.5 sm:gap-1">
                <h4 className="font-bold text-[var(--card-title)]">{title}</h4>
                {/* Status */}
                <div className="text-xs sm:text-sm text-[var(--card-subtext)]">
                  {closedTime}
                </div>
              </div>

              <div className="flex-shrink-0">
                <img
                  src={flagImage}
                  alt={`${country} flag`}
                  className={`border-2 border-[var(--lotto-card-flag-border)] w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain ${
                    !isGameOpen ? 'grayscale' : ''
                  }`}
                />
              </div>
            </div>

            {/* Timer */}
            {timeRemaining && !isOverdue && (
              <>
                <hr className="border-t border-[var(--card-divider)] my-1 sm:my-2" />
                <CountdownTimer
                  datetime={timeRemaining}
                  setIsOverdue={setIsOverdue}
                />
              </>
            )}

            {/* Play Button */}
            <div
              className={`play-btn mt-2 sm:mt-4 uppercase text-center ${
                isGameOpen
                  ? 'active-gradient cursor-pointer'
                  : 'disabled-gradient cursor-not-allowed'
              }`}
            >
              {isGameOpen
                ? t('lottoPage.status.open')
                : t('lottoPage.status.close')}
            </div>
          </div>
        </div>
      </Link>
    </div>
  )
}

export default LottoGameCard
